import { isNil } from 'lodash';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { RRule } from 'rrule';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTextBody1 } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { I18nKey, i18n } from '@shared/util/i18n';

interface Props {
  rruleString: string | undefined;
  onChange(count: RRule | undefined): void;
  selectionTypePluralKey: I18nKey;
}

export function IntervalPicker({ rruleString, onChange, selectionTypePluralKey }: Props) {
  const colors = useContext(ColorThemeContext);
  const rule = useMemo(
    () => (!isNil(rruleString) ? RRule.fromString(rruleString) : undefined),
    [rruleString],
  );

  const [value, setValue] = useState(rule?.options.interval?.toString() ?? '');

  const handleChange = useCallback(
    (updatedValue: string) => {
      const cleanedValue = updatedValue.replace(/\D/g, '');
      setValue(cleanedValue);

      if (!cleanedValue) {
        return;
      }
      const interval = Number(cleanedValue);
      const options = rule?.options ?? {};
      onChange(new RRule({ ...options, interval }));
    },
    [rule, onChange],
  );

  return (
    <View padding={2}>
      <View row centerVertically>
        <MBIconV2 name="repeat" size={grid(4)} color={colors.accent} />
        <MBTextInput
          noBorder
          selectTextOnFocus
          style={[styles.input, { backgroundColor: colors.backgroundInactive }]}
          keyboardType="number-pad"
          autoFocus
          value={value}
          onChangeText={handleChange}
        />
        <MBTextBody1 color={colors.foregroundActive}>
          {i18n(selectionTypePluralKey, { count: Number(value) })}
        </MBTextBody1>
      </View>
    </View>
  );
}

export const styles = StyleSheet.create({
  input: {
    paddingVertical: grid(1),
    paddingHorizontal: grid(2),
    borderRadius: grid(1),
    width: grid(20),
    marginHorizontal: grid(1),
  },
});
